import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/feature/SEOHead';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'True North Vacations',
  description: 'Port Severn\'s premier luxury vacation rental management company specializing in waterfront properties on Georgian Bay, Ontario.',
  url: `${import.meta.env.VITE_SITE_URL || ''}`,
  telephone: '+17055382000',
  email: 'info@truenorthvacations.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Port Severn',
    addressRegion: 'ON',
    addressCountry: 'CA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 44.8167,
    longitude: -79.7833,
  },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '16:00' },
  ],
  areaServed: { '@type': 'Place', name: 'Port Severn, Ontario, Canada' },
  serviceType: 'Vacation Rental Property Management',
  priceRange: '$$',
};

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="True North Vacations | Port Severn Luxury Waterfront Rental Management"
        description="True North Vacations offers premier luxury vacation rental management in Port Severn, Ontario. Waterfront property management, professional maintenance, multi-platform listing on Airbnb, VRBO & Expedia, and 24/7 guest services. Maximize your rental income."
        keywords="Port Severn vacation rentals, waterfront property management Ontario, luxury cottage rental management, Airbnb property management, VRBO management Port Severn"
        canonical={`${import.meta.env.VITE_SITE_URL || ''}/`}
        ogTitle="True North Vacations | Port Severn Luxury Waterfront Rental Management"
        ogDescription="Premier waterfront property management in Port Severn. Full-service care, maximum returns, professional maintenance."
        schemaJson={homeSchema}
      />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen ? 'bg-white shadow-md' : 'bg-[#0A2540]/85 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className={`ri-compass-3-line text-2xl ${scrolled || menuOpen ? 'text-[#0A2540]' : 'text-white'}`}></i>
              <span className={`text-base sm:text-xl font-serif font-semibold ${scrolled || menuOpen ? 'text-[#0A2540]' : 'text-white'}`}>
                True North Vacations
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <Link to="/listings" className={`text-sm font-light transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Properties</Link>
              <Link to="/why-list" className={`text-sm font-light transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>For Owners</Link>
              <Link to="/services" className={`text-sm font-light transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Services</Link>
              <Link to="/owners-portal" className={`text-sm font-light transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Owner Portal</Link>
              <Link to="/contact" className={`text-sm font-light transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Contact</Link>
              <Link to="/contact" className="bg-[#C9A961] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors whitespace-nowrap">
                List Your Property
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              className={`lg:hidden p-2 rounded-md cursor-pointer ${scrolled || menuOpen ? 'text-[#0A2540]' : 'text-white'}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="lg:hidden pt-4 pb-4 border-t border-gray-100 mt-4 space-y-3">
              <Link to="/listings" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Properties</Link>
              <Link to="/why-list" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">For Owners</Link>
              <Link to="/services" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Services</Link>
              <Link to="/owners-portal" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Owner Portal</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Contact</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="inline-block bg-[#C9A961] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors mt-2">
                List Your Property
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[100svh] flex items-start justify-center overflow-hidden pt-32 sm:pt-40 lg:pt-48">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/2adff1c005ba6912ff4b68ca24860f2b.jpg"
            alt="Port Severn Waterfront"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 w-full max-w-5xl mx-auto">
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-4 sm:mb-6 leading-tight drop-shadow-lg">
            Elevate Your Waterfront Investment
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white font-light mb-3 drop-shadow-md">
            Professional Management, Maximum Returns
          </p>
          <p className="text-sm sm:text-base text-white mb-8 sm:mb-12 font-medium drop-shadow-md">
            Port Severn's Premier Luxury Vacation Rental Management
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-[#C9A961] text-white px-8 py-4 rounded-xl text-base font-medium hover:bg-[#B8984E] transition-all flex items-center justify-center space-x-2 whitespace-nowrap"
            >
              <span>Become a Partner Owner</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/listings"
              className="w-full sm:w-auto border-2 border-white text-white px-8 py-4 rounded-xl text-base font-medium hover:bg-white/10 transition-all text-center whitespace-nowrap"
            >
              View Our Properties
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white/70 text-3xl"></i>
        </div>
      </section>

      {/* Why List With Us Section */}
      <section className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="relative">
              <div className="bg-[#0A2540] rounded-3xl p-4 sm:p-8">
                <img
                  src="https://readdy.ai/api/search-image?query=elegant%20luxury%20waterfront%20cottage%20exterior%20with%20pristine%20landscaping%20modern%20architecture%20large%20windows%20overlooking%20serene%20lake%20professional%20property%20maintenance%20high%20end%20vacation%20rental%20in%20Ontario%20Canada%20beautiful%20natural%20setting%20premium%20quality&width=600&height=800&seq=why1&orientation=portrait"
                  alt="Premium Waterfront Property"
                  className="w-full h-64 sm:h-[500px] lg:h-[600px] object-cover rounded-2xl"
                />
              </div>
            </div>

            <div className="mt-4 lg:mt-0">
              <p className="text-[#C9A961] text-sm font-light tracking-widest uppercase mb-4">FOR PROPERTY OWNERS</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-6 leading-tight">
                Maximize Revenue<br />
                Minimize Effort<br />
                Preserve Value
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-sm sm:text-base">
                <p>
                  At True North Vacations, we understand that your waterfront property is more than just an investment—it's a legacy. Our comprehensive management service ensures your cottage not only generates exceptional rental income but maintains the highest standards of care and presentation.
                </p>
                <p>
                  From meticulous exterior property maintenance to professional interior cleaning, multi-platform listing optimization across Airbnb, VRBO, Expedia, and Google, to seamless guest communication and coordination—we handle every detail so you don't have to.
                </p>
              </div>
              <Link
                to="/services"
                className="inline-flex items-center space-x-2 bg-[#0A2540] text-white px-8 py-4 rounded-full mt-8 hover:bg-[#0A2540]/90 transition-all whitespace-nowrap"
              >
                <span>Discover Our Services</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-16 sm:py-24 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 sm:mb-16 gap-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540]">Complete Property Care</h2>
            <p className="text-gray-600 text-base">From maintenance to marketing, we handle every detail</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: 'ri-home-gear-line', title: 'Exterior & Interior Maintenance', description: 'Professional team ensures your property stays in pristine condition year-round' },
              { icon: 'ri-sparkling-2-line', title: 'Premium Cleaning Services', description: 'Deep cleaning before every guest arrival, maintaining 5-star standards' },
              { icon: 'ri-global-line', title: 'Multi-Platform Listing', description: 'Maximum exposure across Airbnb, VRBO, Expedia, Google, and more' },
              { icon: 'ri-customer-service-2-line', title: '24/7 Guest Communication', description: 'Round-the-clock guest coordination and support for seamless experiences' }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 flex items-center justify-center mb-5">
                  <i className={`${service.icon} text-4xl sm:text-5xl text-[#C9A961]`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#0A2540] mb-3 text-center">{service.title}</h3>
                <p className="text-gray-600 text-center leading-relaxed text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Listings Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-5xl font-serif font-bold text-[#0A2540] mb-4">Featured Waterfront Properties</h2>
            <p className="text-[#C9A961] text-base sm:text-lg font-light">Discover luxury living on Port Severn's pristine shores</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-12">
            {[
              { name: 'Sunset Bay Retreat', location: 'Port Severn, Ontario', description: 'Stunning 4-bedroom waterfront cottage with private dock and panoramic lake views', price: '450', image: 'https://readdy.ai/api/search-image?query=luxury%20waterfront%20cottage%20with%20large%20deck%20overlooking%20calm%20blue%20lake%20modern%20exterior%20design%20with%20floor%20to%20ceiling%20windows%20private%20wooden%20dock%20sunset%20lighting%20high%20end%20vacation%20rental%20property%20in%20Ontario%20simple%20clean%20background&width=800&height=600&seq=listing1&orientation=landscape' },
              { name: 'Lakeshore Haven', location: 'Port Severn, Ontario', description: 'Elegant 3-bedroom retreat featuring stone fireplace and expansive waterfront deck', price: '395', image: 'https://readdy.ai/api/search-image?query=elegant%20lakefront%20vacation%20home%20with%20stone%20exterior%20and%20wooden%20accents%20cozy%20cottage%20style%20architecture%20surrounded%20by%20pine%20trees%20peaceful%20waterfront%20setting%20premium%20rental%20property%20natural%20landscape%20simple%20clean%20background&width=800&height=600&seq=listing2&orientation=landscape' },
              { name: 'Crystal Waters Estate', location: 'Port Severn, Ontario', description: 'Luxurious 5-bedroom property with hot tub, fire pit, and 200ft of shoreline', price: '595', image: 'https://readdy.ai/api/search-image?query=upscale%20modern%20lakefront%20estate%20with%20multiple%20levels%20contemporary%20architecture%20large%20outdoor%20living%20spaces%20pristine%20waterfront%20location%20luxury%20vacation%20property%20with%20boat%20house%20high%20end%20design%20simple%20clean%20background&width=800&height=600&seq=listing3&orientation=landscape' }
            ].map((property, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="relative h-52 sm:h-64">
                  <img src={property.image} alt={property.name} className="w-full h-full object-cover" />
                  <div className="absolute top-4 right-4 bg-[#C9A961]/90 text-white px-4 py-1.5 rounded-full text-sm whitespace-nowrap">Available</div>
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-[#0A2540] mb-2">{property.name}</h3>
                  <div className="flex items-center text-gray-500 text-sm mb-3">
                    <i className="ri-map-pin-line mr-1"></i>
                    <span>{property.location}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{property.description}</p>
                  <div className="border-t pt-4 flex items-center justify-between">
                    <div>
                      <span className="text-sm text-gray-500">From </span>
                      <span className="text-xl font-bold text-[#0A2540]">${property.price}</span>
                      <span className="text-sm text-gray-500">/night</span>
                    </div>
                    <Link to="/listings" className="text-[#C9A961] hover:text-[#B8984E] flex items-center space-x-1 text-sm font-medium whitespace-nowrap">
                      <span>View Details</span>
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/listings" className="inline-block border-2 border-[#0A2540] text-[#0A2540] px-8 py-3 rounded-full hover:bg-[#0A2540] hover:text-white transition-all whitespace-nowrap">
              Explore All Properties
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="bg-[#0A2540] rounded-[32px] overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
              <div className="relative h-64 sm:h-80 lg:h-full lg:min-h-[500px]">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20property%20owner%20portrait%20standing%20by%20waterfront%20cottage%20confident%20successful%20person%20outdoor%20setting%20natural%20lighting%20premium%20real%20estate%20investment%20lifestyle%20simple%20neutral%20background&width=600&height=700&seq=testimonial1&orientation=portrait"
                  alt="Property Owner"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 sm:p-12 lg:p-16">
                <i className="ri-double-quotes-l text-5xl sm:text-6xl text-[#C9A961] mb-6 block"></i>
                <p className="text-white text-lg sm:text-2xl leading-relaxed mb-8 font-light">
                  Since partnering with True North Vacations, our rental income has increased by 40% while our workload has dropped to zero. They treat our cottage like their own, and the quality of maintenance has actually improved our property value.
                </p>
                <div className="mb-8">
                  <p className="text-white text-lg font-medium">Michael & Sarah Thompson</p>
                  <p className="text-[#C9A961] text-sm">Waterfront Property Owner, Port Severn</p>
                </div>
                <Link to="/why-list" className="inline-block bg-[#C9A961] text-[#0A2540] px-8 py-3 rounded-full hover:bg-[#B8984E] transition-all font-medium whitespace-nowrap">
                  Join Our Owner Community
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative bg-[#F5F1E8] p-10 sm:p-16 lg:p-24 flex flex-col justify-between min-h-[400px] lg:min-h-[600px]">
          <p className="text-gray-600 text-sm tracking-widest uppercase font-light">PROPERTY OWNERS</p>
          <div>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0A2540] leading-none mb-2">List Your</h2>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A2540] leading-none mb-2">Waterfront</h2>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2540] leading-none">Property Today</h2>
          </div>
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <img
              src="https://readdy.ai/api/search-image?query=luxury%20marina%20dock%20with%20boats%20at%20sunset%20waterfront%20lifestyle%20premium%20yacht%20club%20peaceful%20lake%20setting%20golden%20hour%20lighting%20high%20end%20vacation%20destination%20simple%20clean%20composition&width=800&height=1000&seq=cta1&orientation=portrait"
              alt="Marina"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="bg-white p-10 sm:p-16 lg:p-24 flex flex-col justify-center items-center text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-6 leading-tight">
            Ready to Maximize<br />Your Property's Potential?
          </h2>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-10 max-w-lg">
            Getting started is simple. Our professional team will evaluate your property, create a customized management plan, and have you earning premium rental income in no time.
          </p>
          <Link to="/contact" className="inline-flex items-center space-x-2 bg-[#0A2540] text-white px-10 py-4 rounded-full text-lg hover:bg-[#0A2540]/90 transition-all whitespace-nowrap">
            <span>Schedule Consultation</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2540] text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-12">
            <div className="col-span-2 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-compass-3-line text-2xl text-[#C9A961]"></i>
                <span className="text-lg font-serif font-semibold">True North Vacations</span>
              </div>
              <p className="text-gray-400 text-sm">Port Severn's Premier Luxury Vacation Rental Management</p>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services" className="hover:text-white transition-colors">Property Management</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Maintenance</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Guest Services</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Marketing</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">For Owners</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/contact" className="hover:text-white transition-colors">List Your Property</Link></li>
                <li><Link to="/owners-portal" className="hover:text-white transition-colors">Owner Portal</Link></li>
                <li><Link to="/why-list" className="hover:text-white transition-colors">Revenue Calculator</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Properties</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/listings" className="hover:text-white transition-colors">Current Listings</Link></li>
                <li><Link to="/listings" className="hover:text-white transition-colors">Featured Properties</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Booking Inquiry</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-gray-400 text-sm">© 2025 True North Vacations. All rights reserved.</p>
              <div className="flex items-center space-x-5">
                <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-facebook-fill text-xl"></i></a>
                <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-instagram-line text-xl"></i></a>
                <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-linkedin-fill text-xl"></i></a>
                <a href="mailto:info@truenorthvacations.com" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-mail-line text-xl"></i></a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
